import React, { useState } from 'react';
import { AlertCircleIcon, Loader2Icon, WifiOffIcon } from 'lucide-react';
import { Sheet } from '../Sheet';
import { Button } from '../../ui/Button';
import { TextField } from '../../ui/Field';
import { useAdminData } from '../../../contexts/AdminDataContext';
import { useOnline } from '../../../hooks/useSyncStatus';
import { clearTimerDoc } from '../../../services/timerService';
import type { RunningTimer } from '../../../services/timerService';
import type { CaseEntry } from '../../../types/caseEntry';
import { clockTime, hoursBetween, isoDay, roundToQuarter } from '../../../utils/timer';
import { parseAmount, toInput } from '../case/useCaseEntryForm';
import { debugErrorMessage, logFirebaseError } from '../../../lib/firebase';
interface StopTimerDialogProps {
  open: boolean;
  timer: RunningTimer | null;
  onClose: () => void;
}
export function StopTimerDialog({
  open,
  timer,
  onClose
}: StopTimerDialogProps) {
  return <Sheet open={open} title="Stop timer" onClose={onClose} data-id="element-614" data-mp="1vxi8blrkh0g8">
      {timer && <StopTimerForm key={timer.timerId} timer={timer} onDone={onClose} data-id="element-615" data-mp="3prdcoltt6t6h" />}
    </Sheet>;
}
function StopTimerForm({
  timer,
  onDone
}: {
  timer: RunningTimer;
  onDone: () => void;
}) {
  const {
    cases,
    updateCase,
    settings,
    user
  } = useAdminData();
  const caseItem = cases.find(item => item.id === timer.caseId);
  const online = useOnline();
  const startedAt = new Date(timer.startedAt);
  const endedAt = new Date();
  const [date, setDate] = useState(isoDay(startedAt));
  const [start, setStart] = useState(clockTime(startedAt));
  const [end, setEnd] = useState(clockTime(endedAt));
  const [duration, setDuration] = useState(toInput(roundToQuarter((endedAt.getTime() - startedAt.getTime()) / 3_600_000)));
  const [description, setDescription] = useState(timer.caseTitle);
  const [rate, setRate] = useState((caseItem?.hourlyRate || settings.hourlyRate || '').replace('.', ','));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  function updateRange(nextStart: string, nextEnd: string) {
    const hours = hoursBetween(nextStart, nextEnd);
    if (hours !== null) setDuration(toInput(roundToQuarter(hours)));
  }
  async function handleSave(event: React.FormEvent) {
    event.preventDefault();
    if (saving || !user) return;
    const hours = parseAmount(duration);
    const unitPrice = parseAmount(rate || '0');
    if (!caseItem) {
      setError('Sagen findes ikke længere. Kassér timeren i stedet.');
      return;
    }
    if (!description.trim()) return setError('Skriv en kort beskrivelse.');
    if (!Number.isFinite(hours) || hours <= 0) return setError('Angiv varigheden i timer, fx 2,75.');
    if (!Number.isFinite(unitPrice) || unitPrice < 0) return setError('Angiv en gyldig timepris.');
    setSaving(true);
    setError('');
    try {
      const entries = caseItem.entries ?? [];
      // Timerens id bruges som linjens id — et nyt forsøg kan aldrig give to linjer.
      if (!entries.some(entry => entry.id === timer.timerId)) {
        const now = new Date().toISOString();
        const entry: CaseEntry = {
          id: timer.timerId,
          type: 'Timer',
          date,
          description: `${description.trim()} · ${start}–${end}`,
          quantity: hours,
          unit: 'timer',
          unitPrice,
          vatRate: 25,
          createdAt: now,
          updatedAt: now
        };
        const result = await updateCase(caseItem.id, {
          entries: [...entries, entry]
        });
        if (!result.ok) {
          setSaving(false);
          setError(result.error ?? 'Tiden kunne ikke gemmes. Timeren kører videre.');
          return;
        }
      }
      // Først når tiden er gemt på sagen, fjernes timeren.
      await clearTimerDoc(user.uid);
      setSaving(false);
      onDone();
    } catch (caught) {
      logFirebaseError('stopTimer', caught);
      setSaving(false);
      setError(debugErrorMessage('stopTimer', caught));
    }
  }
  async function handleDiscard() {
    if (!user || saving) return;
    if (!window.confirm('Kassér timeren uden at gemme tiden på sagen?')) return;
    try {
      await clearTimerDoc(user.uid);
      onDone();
    } catch (caught) {
      logFirebaseError('discardTimer', caught);
      setError(debugErrorMessage('discardTimer', caught));
    }
  }
  return <form onSubmit={handleSave} noValidate className="space-y-4" data-id="element-616" data-mp="33kt2yuracd2y">
      <p className="text-[15px] text-ink-soft" data-id="element-617" data-mp="33iztl7pafer9">
        {timer.caseTitle || 'Sag'} — ret gerne tiden, før den gemmes.
      </p>
      <TextField id="timer-dato" label="Dato" type="date" value={date} onChange={e => setDate(e.target.value)} data-id="element-618" data-mp="2tmn12d5hb6y3" />
      <div className="grid grid-cols-3 gap-3" data-id="element-619" data-mp="2fnna2yuhufyr">
        <TextField id="timer-start" label="Start" type="time" value={start} onChange={e => {
        setStart(e.target.value);
        updateRange(e.target.value, end);
      }} data-id="element-620" data-mp="2foyen0vrjrqx" />
        <TextField id="timer-slut" label="Slut" type="time" value={end} onChange={e => {
        setEnd(e.target.value);
        updateRange(start, e.target.value);
      }} data-id="element-621" data-mp="ivde45bmmyfy" />
        <TextField id="timer-varighed" label="Timer" inputMode="decimal" value={duration} onChange={e => setDuration(e.target.value)} data-id="element-622" data-mp="1yfmdtbvsan5n" />
      </div>
      <p className="-mt-2 text-xs text-ink-muted" data-id="element-623" data-mp="289w6kuon1ngw">Varigheden er rundet til nærmeste kvarter.</p>
      <TextField id="timer-beskrivelse" label="Beskrivelse" value={description} onChange={e => setDescription(e.target.value)} data-id="element-624" data-mp="2m1j7x8xftm6j" />
      <TextField id="timer-timepris" label="Timepris (kr.)" inputMode="decimal" value={rate} onChange={e => setRate(e.target.value)} data-id="element-625" data-mp="2w1a0h52ftr3z" />

      {error && <p role="alert" className="flex items-start gap-2 text-sm text-danger" data-id="element-626" data-mp="2r38hsl8xbtt1">
          <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-627" data-mp="11obrcosh50i8" />
          {error}
        </p>}
      {saving && !online && <p role="status" className="flex items-start gap-2 text-sm text-ink-soft" data-id="element-628" data-mp="1mcl4tslh6zj3">
          <WifiOffIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-629" data-mp="1mexukrxh6e6p" />
          Venter på forbindelse – gemmes automatisk
        </p>}

      <Button type="submit" size="lg" className="w-full" disabled={saving} data-id="element-630" data-mp="1vc852b2gxzak">
        {saving ? <>
            <Loader2Icon className="h-5 w-5 animate-spin" aria-hidden="true" data-id="element-631" data-mp="20d5m2kbqsnah" />
            Gemmer …
          </> : 'Gem timer på sagen'}
      </Button>
      <button type="button" onClick={handleDiscard} disabled={saving} className="w-full py-2 text-center text-sm text-ink-muted transition-colors duration-150 hover:text-danger disabled:opacity-50" data-id="element-632" data-mp="29c99g0hxzous">
        Kassér timer
      </button>
    </form>;
}